/**
 * Поэтапное открытие новых лендингов для поисковика.
 *
 * Зачем: девять страниц каталога написаны разом, но выкатывать их в индекс
 * одним днём не стоит — резкий прирост однотипных URL на молодом кластере
 * читается как Scaled Content Abuse. Поэтому страницы открываются по одной
 * раз в RELEASE_INTERVAL_DAYS дней.
 *
 * Как устроено (важно): состояния НЕТ. Сколько страниц открыто — это чистая
 * функция от текущего времени и константы RELEASE_START. Ни счётчика в памяти,
 * ни записи в БД, ни крона. Поэтому перезапуск или падение сервера ничего не
 * сбивает: поднявшись через час или через неделю, процесс посчитает ровно то же
 * число, что и любой другой процесс в этот момент. По той же причине несколько
 * инстансов за балансировщиком всегда согласованы между собой.
 *
 * "Открыта" значит: страница существует. Она попадает в sitemap.xml, отдаётся
 * с кодом 200, появляется плиткой на хабе /widgets и участвует в перелинковке.
 *
 * Ещё не открытая страница не существует: прямой запрос к ней отдаёт 404,
 * ровно как несуществующий slug. Промежуточного состояния «доступна, но с
 * noindex» нет намеренно — робот, добравшийся до неё любым путём, не должен
 * получить ничего, кроме 404.
 *
 * Посмотреть ещё не открытые страницы можно локально (там открыто всё) или
 * подняв прод с WIDGETS_RELEASE_ALL=1.
 *
 * Локально и в dev-сборке открыто всё сразу — см. isEveryPageOpen().
 *
 * @see docs/seo/SEO-STRATEGY.md
 */

/** Через сколько дней открывается следующая страница. */
export const RELEASE_INTERVAL_DAYS = 2;

/**
 * Момент, когда открывается ПЕРВАЯ страница из очереди.
 * До этой даты не открыта ни одна; ставить нужно дату релиза сайта.
 * UTC указан явно, чтобы результат не зависел от таймзоны сервера.
 */
export const RELEASE_START = '2026-09-14T09:00:00Z';

/**
 * Очередь публикации — порядок, в котором страницы открываются.
 *
 * Сюда входит ВСЁ новое на сайте: лендинги каталога (с 30.09.2026 — и
 * новые виджеты 4.0.0), «как поменять новую вкладку», статья про фон
 * Chrome и обзор расширений.
 * Старые страницы (главная, /themes, /pro, /about, /support, /top) в очереди
 * не участвуют — их мы только переписали, а не создали, и закрывать их
 * от индексации нет причин.
 *
 * Порядок — по ожидаемому спросу: то, по чему мы хотим встать раньше, сверху.
 * Менять порядок и вставлять новые id можно только ПОСЛЕ уже открытых
 * страниц (см. /release-status.json): вставка перед открытой сдвинет её
 * дальше по графику, и она "закроется" обратно с 404.
 *
 * type: 'widget' — id это slug лендинга каталога;
 *       'page'   — id это путь отдельной страницы.
 */
export const STAGED_PAGES = [
	{ id: 'countdown', type: 'widget' },
	{ id: 'todo-list', type: 'widget' },
	{ id: 'wallpaper', type: 'widget' },
	{ id: 'clock', type: 'widget' },
	{ id: 'habit-tracker', type: 'widget' },
	{ id: 'notes', type: 'widget' },
	{ id: '/how-to-change-new-tab', type: 'page' },
	{ id: 'pomodoro', type: 'widget' },
	{ id: 'weather', type: 'widget' },
	{ id: 'image', type: 'widget' },
	{ id: 'bookmarks', type: 'widget' },
	{ id: '/best-new-tab-chrome-extensions', type: 'page' },
	{ id: 'search-bar', type: 'widget' },
	{ id: 'calendar', type: 'widget' },
	{ id: 'currency', type: 'widget' },
	{ id: 'top-sites', type: 'widget' },
	{ id: 'workspaces', type: 'widget' },
	// 30.09.2026: здесь стоял dictionary — виджет удалён из расширения
	// до релиза 4.0.0, страница снята. Слот занят статьёй про фон:
	// самый большой спрос из исследования (docs/seo/research/README.md).
	{ id: '/how-to-change-chrome-background', type: 'page' },
	{ id: 'quotes', type: 'widget' },
	// Новые виджеты 4.0.0 вставлены между ещё не открытыми страницами,
	// а не в хвост: иначе они ждали бы до декабря. Открытых страниц
	// (позиции 1–9 на 30.09) вставка не касается.
	{ id: 'daily-focus', type: 'widget' },
	{ id: 'link', type: 'widget' },
	{ id: 'google-calendar', type: 'widget' },
	{ id: 'news', type: 'widget' },
	{ id: 'count-up', type: 'widget' },
	{ id: 'text', type: 'widget' },
	{ id: 'facts', type: 'widget' },
	{ id: 'workday-countdown', type: 'widget' },
	{ id: 'flash-cards', type: 'widget' },
	{ id: 'chess-puzzle', type: 'widget' },
	{ id: 'analog-clock', type: 'widget' },
	{ id: 'figure', type: 'widget' },
	{ id: 'iframe', type: 'widget' },
	{ id: 'flip-clock', type: 'widget' },
	{ id: 'date', type: 'widget' },
	{ id: 'calculator', type: 'widget' },
	{ id: 'google-apps', type: 'widget' },
	{ id: 'timetable', type: 'widget' },
	{ id: 'canvas', type: 'widget' },
	{ id: 'day-countdown', type: 'widget' },
];

/** Хаб каталога. Открывается вместе с первым лендингом — см. isHubReleased. */
export const HUB_PATH = '/widgets';

/** Только id, в порядке очереди. */
export const STAGED_IDS = STAGED_PAGES.map(page => page.id);

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Открыты ли сразу все страницы.
 *
 * - в dev-сборке (`yarn start`) — да, чтобы локально видеть весь каталог;
 * - на проде — да, если выставлен WIDGETS_RELEASE_ALL=1 (аварийный рубильник,
 *   если график больше не нужен и всё пора открыть разом);
 * - иначе работает график.
 *
 * `__DEV__` подставляется webpack'ом в оба бандла; typeof-проверка нужна для
 * jest, который собирает модуль без DefinePlugin.
 *
 * @returns {boolean}
 */
export function isEveryPageOpen() {
	if (typeof __DEV__ !== 'undefined' && __DEV__) {
		return true;
	}

	if (
		typeof process !== 'undefined' &&
		process.env &&
		process.env.WIDGETS_RELEASE_ALL === '1'
	) {
		return true;
	}

	return false;
}

/**
 * Сколько страниц открыл СЕРВЕР при рендере этой страницы.
 *
 * Зачем: роуты изоморфные, и в браузере тот же код считал бы график заново —
 * по часам пользователя и без доступа к process.env. Это давало расхождение
 * SSR и гидратации (сервер рисует 33 плитки, браузер перерисовывает в 24),
 * а у пользователя со спешащими часами открывало бы страницы раньше срока.
 * Поэтому решение принимает сервер, а клиент его просто читает.
 *
 * @returns {number|null} null — мы на сервере или значение не передали
 */
function getServerOpenPageCount() {
	if (
		typeof window !== 'undefined' &&
		window.App &&
		typeof window.App.widgetsOpen === 'number'
	) {
		return window.App.widgetsOpen;
	}

	return null;
}

/**
 * Сколько страниц из очереди уже открыто на момент now.
 *
 * Ровно в RELEASE_START открывается первая, дальше по одной каждые
 * RELEASE_INTERVAL_DAYS дней. Больше длины очереди не вернёт.
 *
 * В браузере значение берётся от сервера — см. getServerOpenPageCount().
 *
 * @param {number|Date} [now]
 * @returns {number} 0..STAGED_IDS.length
 */
export function getOpenPageCount(now = Date.now()) {
	const fromServer = getServerOpenPageCount();

	if (fromServer !== null) {
		return Math.min(Math.max(fromServer, 0), STAGED_IDS.length);
	}

	if (isEveryPageOpen()) {
		return STAGED_IDS.length;
	}

	const start = Date.parse(RELEASE_START);

	// Сломанная константа не должна прятать весь каталог: считаем всё открытым
	if (Number.isNaN(start)) {
		return STAGED_IDS.length;
	}

	const elapsed = (now instanceof Date ? now.getTime() : now) - start;

	if (elapsed < 0) {
		return 0;
	}

	const opened = Math.floor(elapsed / (RELEASE_INTERVAL_DAYS * DAY_MS)) + 1;

	return Math.min(opened, STAGED_IDS.length);
}

/**
 * Входит ли страница в очередь поэтапного открытия.
 * Страницы, существовавшие до этой итерации, в очереди не участвуют
 * и открыты всегда.
 *
 * @param {string} id slug лендинга или путь страницы
 * @returns {boolean}
 */
export function isStaged(id) {
	return STAGED_IDS.indexOf(id) !== -1;
}

/**
 * Открыта ли страница.
 *
 * @param {string} id slug лендинга или путь страницы
 * @param {number|Date} [now]
 * @returns {boolean}
 */
export function isReleased(id, now = Date.now()) {
	const position = STAGED_IDS.indexOf(id);

	if (position === -1) {
		return true; // не из очереди — открыта всегда
	}

	return position < getOpenPageCount(now);
}

/**
 * Открыт ли хаб каталога.
 *
 * Хаб — оглавление кластера, и в очереди он не стоит: пустое оглавление
 * бессмысленно, а оглавление из одного пункта выглядит сломанным ровно
 * до появления второго. Поэтому он открывается в тот же момент, что и
 * первый лендинг, и дальше просто растёт вместе с очередью.
 *
 * @param {number|Date} [now]
 * @returns {boolean}
 */
export function isHubReleased(now = Date.now()) {
	return getOpenPageCount(now) >= 1;
}

/**
 * Дата страницы по графику: когда она открылась или когда откроется.
 * Не зависит от текущего времени — это позиция в очереди, а не состояние.
 * Для страниц вне очереди — null.
 *
 * Нужна карте сайта: у открывшейся страницы `lastmod` должен быть днём её
 * появления, а не датой правки каталога. Раньше здесь была только
 * `getOpenDate`, которая для уже открытых страниц возвращает null, и карта
 * тихо подставляла всем одну и ту же дату — более раннюю, чем страница
 * вообще стала доступна.
 *
 * @param {string} id
 * @returns {Date|null}
 */
export function getReleaseDate(id) {
	const position = STAGED_IDS.indexOf(id);

	if (position === -1) {
		return null;
	}

	return new Date(
		Date.parse(RELEASE_START) + position * RELEASE_INTERVAL_DAYS * DAY_MS,
	);
}

/**
 * Когда откроется страница. Для ещё не открытых — момент открытия,
 * для уже открытых и для страниц вне очереди — null.
 *
 * @param {string} id
 * @param {number|Date} [now]
 * @returns {Date|null}
 */
export function getOpenDate(id, now = Date.now()) {
	if (isReleased(id, now)) {
		return null;
	}

	return getReleaseDate(id);
}

/**
 * Дата открытия последней вышедшей страницы. Это момент, когда в последний
 * раз изменился состав карты сайта и содержимое хаба.
 *
 * @param {number|Date} [now]
 * @returns {Date|null}
 */
export function getLastOpenedDate(now = Date.now()) {
	const open = getOpenPageCount(now);

	return open < 1 ? null : getReleaseDate(STAGED_IDS[open - 1]);
}

/**
 * Сколько лендингов каталога ещё закрыто. Нужно счётчику на главной,
 * который не должен обещать больше виджетов, чем показывает хаб.
 *
 * @param {number|Date} [now]
 * @returns {number}
 */
export function getClosedWidgetCount(now = Date.now()) {
	return STAGED_PAGES.filter(
		page => page.type === 'widget' && !isReleased(page.id, now),
	).length;
}

/**
 * Состояние графика целиком — для /release-status.json и логов.
 *
 * @param {number|Date} [now]
 * @returns {Object}
 */
export function getReleaseSchedule(now = Date.now()) {
	const open = getOpenPageCount(now);

	return {
		open,
		total: STAGED_IDS.length,
		everyPageOpen: isEveryPageOpen(),
		hubOpen: isHubReleased(now),
		start: RELEASE_START,
		intervalDays: RELEASE_INTERVAL_DAYS,
		finishesAt: new Date(
			Date.parse(RELEASE_START) +
				(STAGED_IDS.length - 1) * RELEASE_INTERVAL_DAYS * DAY_MS,
		).toISOString(),
		pages: STAGED_PAGES.map((page, i) => ({
			id: page.id,
			type: page.type,
			position: i + 1,
			released: i < open,
			opensAt: new Date(
				Date.parse(RELEASE_START) + i * RELEASE_INTERVAL_DAYS * DAY_MS,
			).toISOString(),
		})),
	};
}
